import { Button } from 'components';
import { theme } from 'configs/styled';
import { useIsWebView } from 'hooks/useIsWebView';
import usePrompt from 'hooks/usePrompt';
import { useQueryParams } from 'hooks/useQueryParams';
import React, { useEffect, useState } from 'react';
import { Action, useMutation } from 'react-fetching-library';
import { authenticationService, mainService } from 'services/driverPortal';
import {
  APPROVE_STATUS,
  IDocument,
  IDriverDocumentsReview,
  IUpdateReviewStatusRequest,
  IUpdateReviewStatusResponse,
  MENU_STATUSES,
  STATUS_RESPONSE,
} from 'services/driverPortal/DriverPortal.interface';
import {
  getDocumentsReviewMessage,
  mapDocumentsReviewStatus,
} from 'utils/documentRejected';
import {
  IMutationResponseError,
  transformMutationResponseError,
} from 'utils/transformResponseError';
import Loading from '../../../components/ReturnOrderModal/Loading';
import {
  IMenuItem,
  PotentialLeadData,
} from '../../../services/driverPortal/main/MainService.interface';
import Alert from '../components/Alert';
import Divider from '../components/Divider';
import DriverPortalLayout from '../components/DriverPortalLayout';
import HeaderPortal from '../components/HeaderPortal';
import TextRegular from '../components/TextRegular';
import Title from '../components/Title';
import { Status } from './components/DocumentStatus';
import DocumentsReviewStatus from './components/DocumentsReviewStatus';
import Logout from './components/Logout';
import MenuList from './components/Menu';

export interface IMainPageProps {}

interface IMainMenuProps {
  driverDocumentsReview: IDriverDocumentsReview;
  onReload: () => void;
}

function getRequiredDocuments(documents: IDocument[]) {
  return documents.filter(document => document.required);
}

function getPendingDocuments(documents: IDocument[]) {
  return documents.filter(
    document => document.status === MENU_STATUSES.PENDING,
  );
}

function isWaitingApprove(approveStatus: APPROVE_STATUS) {
  return approveStatus === APPROVE_STATUS.WAITING_APPROVE;
}

function allowSubmitDocument(documents: IDocument[]) {
  const requiredDocuments = getRequiredDocuments(documents);

  const pendingDocuments = getPendingDocuments(requiredDocuments);

  return pendingDocuments.length === 0;
}

const MainMenu: React.FC<IMainMenuProps> = props => {
  const { search, pushUrl } = useQueryParams();
  const isWebView = useIsWebView();

  const { userId } = authenticationService.getUserToken();
  const [auth, setAuth] = useState(() => authenticationService.getUserToken());
  const { driverDocumentsReview, onReload } = props;
  const {
    documents,
    documentsReviewStatus,
    approveStatus,
  } = driverDocumentsReview;

  const [showPrompt, setShowPrompt] = usePrompt();

  const updateReviewStatusAction = (
    request: IUpdateReviewStatusRequest,
  ): Action<IUpdateReviewStatusResponse> => {
    return {
      method: 'POST',
      endpoint: `/driver/portal/v1/ondemand/drivers/${userId}/documents/submit`,
      body: request,
    };
  };

  const goToLoginPage = () => {
    pushUrl('/login', search);
  };

  const { mutate } = useMutation(updateReviewStatusAction);

  const [isDisableSubmit, setIsDisableSubmit] = useState<boolean>(
    !allowSubmitDocument(documents),
  );

  useEffect(() => {
    setShowPrompt(false);
  }, [showPrompt, setShowPrompt]);

  const reviewMessage = getDocumentsReviewMessage(documentsReviewStatus);

  const handlerMutateError = async (
    errorStatus?: number,
    payload?: IUpdateReviewStatusResponse,
  ) => {
    if (errorStatus === STATUS_RESPONSE.UNAUTHORIZED) {
      await authenticationService.logout();
      goToLoginPage();
    } else {
      transformMutationResponseError(payload as IMutationResponseError);
    }
  };

  const handleClickSubmitDocumentReview = async () => {
    const { error: mutateError, status, payload } = await mutate({
      type: 'save',
    });
    if (mutateError) {
      await handlerMutateError(status, payload);
    } else {
      setIsDisableSubmit(true);
      onReload();
    }
  };

  const shouldDisableButtonSubmit = () => {
    return isDisableSubmit;
  };

  const shouldHideButtonSubmit = () => {
    return isWaitingApprove(approveStatus);
  };

  return (
    <>
      <>
        {!isWebView && reviewMessage && (
          <Alert
            type={mapDocumentsReviewStatus(approveStatus)}
            message={reviewMessage}
            style={{ marginBottom: theme.spacing.xl }}
          />
        )}

        <Title>ส่งข้อมูลและเอกสาร</Title>

        {isWebView ? (
          <TextRegular>
            หากมีการแก้ใขช้อมูล ระบบจะใช้เวลาดำเนินการ 3-7 วันทำการ
            ในระหว่างดำเนินการ อาจมีการติดต่อจากเจ้าหน้าที่เพื่อ
            สอบถามข้อมูลเพิ่มเติม
          </TextRegular>
        ) : (
          <TextRegular>
            กรุณากรอกข้อมูล และอัพโหลดเอกสารที่มีเครื่องหมาย * ให้ครบถ้วน แล้วกด
            &quot;ส่งข้อมูลและเอกสาร&quot; เพื่อยืนยันการสมัครสมาชิก
            หากมีข้อสงสัยในการใช้งานกรุณาติดต่อเจ้าหน้าที่ 02-826-7995
          </TextRegular>
        )}
        {/*<MenuList*/}
        {/*  menu={documents.filter(item => item.code !== 'vehicleInsurance')}*/}
        {/*/>*/}

        {!isWebView && (
          <>
            <Divider />

            <Button
              block
              htmlType="submit"
              disabled={shouldDisableButtonSubmit()}
              type={shouldDisableButtonSubmit() ? 'default' : 'primary'}
              size="large"
              onClick={handleClickSubmitDocumentReview}
              hidden={shouldHideButtonSubmit()}
            >
              ส่งข้อมูลและเอกสาร
            </Button>
          </>
        )}
      </>
    </>
  );
};

const MainPage: React.FC<IMainPageProps> = () => {
  const isWebView = useIsWebView();
  const { search, pushUrl } = useQueryParams();
  const { userId, accessToken, userTel } = authenticationService.getUserToken();

  const mockMenuList: IMenuItem[] = [
    {
      title: 'ข้อมูลส่วนตัว',
      required: true,
      disabled: false,
      path: '/personal-information',
    },
    {
      title: 'รูปถ่ายและบัตรประชาชน',
      required: true,
      disabled: false,
      path: '/identity',
    },
    {
      title: 'ใบขับขี่',
      required: true,
      disabled: false,
      path: '/driver-license',
    },
    {
      title: 'ข้อมูลรถ',
      required: true,
      disabled: false,
      path: '/vehicle-information',
    },
    {
      title: 'หน้าสมุดธนาคารไทยพาณิชย์',
      required: true,
      disabled: false,
      path: '/book-bank',
    },
    {
      title: 'ประกันภาคสมัครใจ',
      required: false,
      disabled: false,
      path: '/voluntary-insurance',
    },
    {
      title: 'ทะเบียนบ้าน',
      required: false,
      disabled: false,
      path: '/house-registration',
    },
    {
      title: 'ใบรับรองแพทย์',
      required: false,
      disabled: false,
      path: '/medical-certificate',
    },
    {
      title: 'อบรมออนไลน์',
      // required: false,
      defaultre: true,
      disabled: false,
      path: '/online-training',
    },
  ];

  const STATUS_MAP: Record<string, Status> = {
    none_data: Status.PENDING,
    waiting: Status.SUBMITTED,
    document_invalied: Status.REJECTED,
    document_invalid: Status.REJECTED,
    approved: Status.APPROVED,
    waiting_online_asssignment: Status.WAITING,
    confirm_online_asssignment: Status.CONFIRM,
  };

  const STATUS_PRIORITY: Status[] = [
    Status.REJECTED,
    Status.PENDING,
    Status.SUBMITTED,
    Status.APPROVED,
  ];

  const pickMostSevereStatus = (statuses: (Status | undefined)[]) => {
    for (const s of STATUS_PRIORITY) {
      if (statuses.includes(s)) return s;
    }
    return undefined;
  };

  const [menuList, setMenuList] = useState<IMenuItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const goToLoginPage = () => {
    pushUrl('/login', search);
  };

  const syncSessionMetadata = (payload: any) => {
    const potentialLead = payload?.potentialLead ?? {};

    authenticationService.setUserSessionMetadata({
      userId:
        payload?.userId ??
        payload?.user_id ??
        payload?.riderId ??
        payload?.rider_id ??
        potentialLead?.userId ??
        potentialLead?.user_id,
      potentialLeadId:
        payload?.potentialLeadId ??
        payload?.potential_lead_id ??
        potentialLead?.potentialLeadId ??
        potentialLead?.id ??
        potentialLead?._id,
      userTel:
        payload?.tel ??
        payload?.phone ??
        payload?.email ??
        potentialLead?.tel ??
        potentialLead?.phone,
    });
  };

  // const { loading, payload, error, query } = useQuery<IMainResponse>({
  //   method: 'GET',
  //   endpoint: `/api/rider/lead-info?potentialLeadId=${userId}`,
  //   headers: {
  //     Authorization: 'Bearer ' + accessToken,
  //   },
  // });

  const getInfo = async () => {
    try {
      const response = await mainService.getInfo();
      const leadInfo = response?.data ?? response;

      if (leadInfo) {
        syncSessionMetadata(leadInfo);
        const newMenuList: IMenuItem[] = [];
        const d = leadInfo;

        const nodeByTitle: Record<string, any> = {
          ข้อมูลส่วนตัว: d.potentialLeadInfo,
          ใบขับขี่: d.potentialLeadDrivingLicense,
          รูปถ่ายและบัตรประชาชน: d.potentialLeadIdCard,
          ข้อมูลรถ: d.potentialLeadVehicleInfo,
          หน้าสมุดธนาคารไทยพาณิชย์: d.potentialLeadBookBank,
          ประกันภาคสมัครใจ: d.potentialLeadVoluntaryInsurance,
          ทะเบียนบ้าน: d.potentialLeadHouseRegistration,
          ใบรับรองแพทย์: d.potentialLeadMedicalCertificate,
          อบรมออนไลน์: d.potentialLeadOnlineAssessment,
        };

        const REQUIRED_FOR_TRAINING = [
          'ข้อมูลส่วนตัว',
          'ใบขับขี่',
          'รูปถ่ายและบัตรประชาชน',
          'ข้อมูลรถ',
          'หน้าสมุดธนาคารไทยพาณิชย์',
        ] as const;

        const isApprovedCode = (code?: keyof typeof STATUS_MAP) => {
          if (!code) return false;
          return STATUS_MAP[code] === Status.APPROVED;
        };

        const isSectionApproved = (title: string, node: any): boolean => {
          if (!node) return false;

          if (title === 'ข้อมูลรถ') {
            const subCodes = [
              node.statusInfoCode,
              node.statusTaxSignCode,
              node.statusActCode,
            ].filter(Boolean) as (keyof typeof STATUS_MAP)[];
            if (subCodes.length === 0) return false;
            return subCodes.every(isApprovedCode);
          }

          const code = node?.statusCode as keyof typeof STATUS_MAP | undefined;
          return isApprovedCode(code);
        };

        const allApprovedForTraining = REQUIRED_FOR_TRAINING.every(title =>
          isSectionApproved(title, nodeByTitle[title]),
        );

        mockMenuList.forEach(item => {
          const node = nodeByTitle[item.title];

          if (item.title === 'ข้อมูลรถ' && node) {
            const vehicleStatusCodes = [
              node.statusInfoCode,
              node.statusTaxSignCode,
              node.statusActCode,
            ];
            const subStatuses = vehicleStatusCodes
              .filter(Boolean)
              .map(code => STATUS_MAP[code as keyof typeof STATUS_MAP]);

            const mostSevere = pickMostSevereStatus(subStatuses);

            if (mostSevere) {
              newMenuList.push({ ...item, status: mostSevere });
              return;
            }

            if (vehicleStatusCodes.every(code => code === null)) {
              newMenuList.push({ ...item, status: Status.REQUIRED });
              return;
            }
          }

          if (item.title === 'อบรมออนไลน์') {
            let trainingCode = node?.statusCode as
              | keyof typeof STATUS_MAP
              | undefined;

            if (trainingCode === 'none_data' || trainingCode == null) {
              if (allApprovedForTraining) {
                newMenuList.push({
                  ...item,
                  status: Status.START_TRAINING,
                  disabled: false,
                });
                return;
              }
              trainingCode = 'waiting_online_asssignment';
            } else if (trainingCode === 'waiting') {
              trainingCode = 'confirm_online_asssignment';
            }

            const trainingStatus = trainingCode
              ? STATUS_MAP[trainingCode]
              : Status.WAITING;

            newMenuList.push({
              ...item,
              status: trainingStatus,
              disabled: !allApprovedForTraining,
            });

            return;
          }

          const code = node?.statusCode as keyof typeof STATUS_MAP | undefined;
          if (code && STATUS_MAP[code]) {
            newMenuList.push({ ...item, status: STATUS_MAP[code] });
          } else if (node == null || node.statusCode == null) {
            newMenuList.push({ ...item, status: Status.REQUIRED });
          } else {
            newMenuList.push({ ...item });
          }
        });

        setMenuList(newMenuList);
      }
    } catch (e) {
      console.log('===== Debug error status: ', e.toString());
      if (e?.status === STATUS_RESPONSE.UNAUTHORIZED) {
        goToLoginPage();
      }
    }
  };

  const PROFILE_KEY = 'driver-portal-profile';

  const getProfile = async (tel: string) => {
    try {
      const res = await mainService.getProfile(tel);
      const payload = res.data;

      syncSessionMetadata(payload);
      localStorage.setItem(PROFILE_KEY, JSON.stringify(payload));
    } catch (e) {
      console.log('===== Debug error status: ', e?.toString?.() ?? e);
      if (e?.status === STATUS_RESPONSE.UNAUTHORIZED) {
        goToLoginPage();
      }
    }
  };

  // const displayComponent = (driverDocumentsReview: IDriverDocumentsReview) => {
  //   const { approveStatus, documentsReviewStatus } = driverDocumentsReview;
  //   if (!isWebView && approveStatus === APPROVE_STATUS.APPROVED) {
  //     return <Approval alertMessage={documentsReviewStatus?.message} />;
  //   }
  //   if (!isWebView && approveStatus === APPROVE_STATUS.TERMINATED) {
  //     return <Rejection />;
  //   }
  //   return (
  //     <MainMenu
  //       onReload={query}
  //       driverDocumentsReview={driverDocumentsReview}
  //     />
  //   );
  // };

  // const handleUnauthorized = useCallback(async error => {
  //   if (error) {
  //     await authenticationService.logout();
  //     goToLoginPage();
  //   }
  // }, []);
  //
  // useEffect(() => {
  //   handleUnauthorized(error);
  // }, [error, handleUnauthorized]);

  useEffect(() => {
    let isMounted = true;

    const loadMainPage = async () => {
      if (!isMounted) return;

      setLoading(true);
      try {
        await Promise.all([getInfo(), getProfile(userTel)]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadMainPage();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <>
      {loading && <Loading />}

      {/*{loading && <Loading />}*/}
      {/*{!error && payload && (*/}
      {/*  <>*/}
      {/*    <HeaderPortal*/}
      {/*      documentsReviewStatusName={*/}
      {/*        payload.data.documentsReviewStatus.status*/}
      {/*      }*/}
      {/*    />*/}

      {/*    <DriverPortalLayout>*/}
      {/*      {displayComponent(payload.data)}*/}
      {/*    </DriverPortalLayout>*/}
      {/*  </>*/}
      {/*)}*/}

      <div>
        <HeaderPortal>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <DocumentsReviewStatus statusName="" />
            <Logout disabledDivider={false} />
          </div>
        </HeaderPortal>

        <DriverPortalLayout>
          <Title>ส่งข้อมูลและเอกสาร</Title>

          <TextRegular>
            กรุณากรอกข้อมูล และอัปโหลดเอกสารในหัวข้อที่มีเครื่องหมาย *
            ให้ครบถ้วน เพื่อยืนยันการสมัครเป็นไรเดอร์
          </TextRegular>

          {/* new menu list*/}
          <MenuList menu={menuList} />
        </DriverPortalLayout>
      </div>
    </>
  );
};

export default MainPage;
